import { fetchUserProfile } from "@/services/profile.service";
import { useAuthStore } from "@/store/auth.store";
import { useProfileStore } from "@/store/profile.store";
import { getCookie, removeCookie } from "@/utils/cookies.utils";

/**
 * Retrieves PSAccess cookie and sets it in the auth store.
 * Fetches user profile if access token is present and sets it in the profile store.
 */
const initAuth = async () => {
  const accessToken = getCookie("PSAccess");

  if (!accessToken) {
    // calling setIsLoggedIn will set authInitialized to true
    useAuthStore.getState().setIsLoggedIn(false);
    useAuthStore.getState().setAuthInitialized();
    return;
  }

  useAuthStore.getState().setAccessToken(accessToken);

  try {
    const profile = await fetchUserProfile(accessToken);
    // if the access token is valid, profile will be returned with an SSOid
    // this is how we determine if the user is authenticated
    const isLoggedIn = !!profile?.SSOid;
    useAuthStore.getState().setIsLoggedIn(isLoggedIn);
  } catch {
    // we want to do the logout process here to ensure all auth and profile state is cleared on failure to fetch profile which likely means the access token is invalid - this will prevent any further errors caused by the invalid token and ensure user is in a logged out state
    logoutUser(false);
  } finally {
    useAuthStore.getState().setAuthInitialized();
  }
};

const removeAuthCookies = () => {
  removeCookie("PSAccess");
  removeCookie("PSName");
  removeCookie("PSState");
  removeCookie("PSRefresh");
  removeCookie("PSUser");
  removeCookie("PS");
};

/**
 * Removes PSAccess cookie and redirects to login page.
 * Removes profile from profile store.
 */
const logoutUser = async (refreshPage: boolean = true) => {
  const accessToken = getCookie("PSAccess");

  const url = "/v2/auth/logout";

  fetch(url, {
    method: "GET",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  removeAuthCookies();

  useAuthStore.getState().logout();
  useProfileStore.getState().clearProfile();

  // reload the page to ensure all state is cleared and user is redirected to the appropriate login page (FA vs non-FA)
  if (refreshPage) {
    window.location.reload();
  }
};

/*
 * The password reset endpoint
 **/
const postPasswordReset = async (email: string): Promise<boolean> => {
  const response = await fetch("/v2/auth/request-password-reset", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
    }),
  });

  if (!response.ok) {
    return false;
  }

  return true;
};

const removePromoCookies = () => {
  removeCookie("promo_code");
  removeCookie("promo-id");
  removeCookie("tm_ent");
};

export {
  initAuth,
  logoutUser,
  postPasswordReset,
  removeAuthCookies,
  removePromoCookies,
};
